import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDropzone } from "react-dropzone";
import { StyledButton } from "../StyledComponents";
import Icon from "./Icons";

const PrescriptionUploadModal = ({
  open,
  handleClose,
  handleSubmitAdd,
  files,
  setFiles,
}) => {
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setFiles([
        Object.assign(file, {
          preview: file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : null,
        }),
      ]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png"],
      "application/pdf": [".pdf"],
    },
    multiple: false,
  });

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      {/* Title */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
        }}
      >
        <Typography variant="h6" sx={{ color: "#125386", fontWeight: 600 }}>
          Upload Handwritten Prescription
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: "#000" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Divider line under header */}
      <Box sx={{ borderBottom: "1px solid #C7C7CC ", mt: 1, mr: 1, ml: 1 }} />
      {/* Content */}
      <DialogContent sx={{ padding: 3, marginTop: 1 }}>
        {
          <>
            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed #1EBBAF",
                borderRadius: 2,
                padding: 3,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive ? "#f0f8ff" : "transparent",
                backgroundImage: "url(/newImages/attachmentsBackground.svg)",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <input {...getInputProps()} />
              {isDragActive ? (
                <Typography>Drop the file here...</Typography>
              ) : (
                <Typography>
                  Drag & drop prescription file here, or click to select
                </Typography>
              )}
            </Box>

            {/* File Preview / Name */}
            {files.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 2,
                  p: 1,
                  border: "1px solid #4DD7CD",
                  borderRadius: 1,
                  backgroundColor: "#E9FAF9",
                }}
              >
                {/* Thumbnail for images */}
                {files[0].preview ? (
                  <img
                    src={files[0].preview}
                    alt={files[0].name}
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: "cover",
                      borderRadius: "4px",
                      marginRight: "10px",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#eee",
                      borderRadius: "4px",
                      marginRight: "10px",
                      fontSize: "12px",
                      color: "#555",
                    }}
                  >
                    PDF
                  </Box>
                )}

                {/* File name */}
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {files[0].name}
                </Typography>

                {/* Remove button */}
                <IconButton
                  onClick={() => setFiles([])}
                  size="small"
                  // sx={{ color: "red" }}
                >
                  <Icon name="deletePrescriptionIcon" size={24} />
                </IconButton>
              </Box>
            )}
          </>
        }
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ padding: 2 }}>
        <StyledButton
          textColor="#FDFDFD"
          bgColor="#85C988"
          onClick={() => {
            {
              handleSubmitAdd(files);
            }
          }}
          variant="contained"
          disabled={files.length === 0}
        >
          Submit
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
};

export default PrescriptionUploadModal;
